import React from 'react';
import { Field, ErrorMessage, getIn, FieldInputProps, FormikProps, useFormikContext } from 'formik';
import { getBallastCbr } from '../../../../services/BallastCbrDatabase';
import { DynamicFormData } from '../../../../types/FormTypes';
import { Box } from '@mui/material';
import { FieldConfig, ValidationService } from '../../../../services/ValidationService';
import SliderWithTextField from '../../../../components/SliderWithTextField';
import DynamicSelectField from './DynamicSelectField';
import FormattedInput from './FormattedInput';
import InfoBoxButton from '../../../../components/InfoBoxButton';
import { renderErrorMessage as defaultRenderErrorMessage } from './utils';
import { useAppSelector } from '../../../../../reduxService/hooks';
import { FormatSymbol } from '../../../../enum/SubscriptSymbol';
import { isPendingRawText, resolveDecimalFieldPolicy } from '../../../../utils/decimalInput';

/**
 * Props interface for FieldRenderer components
 */
export interface FieldRendererProps {
    fieldName?: string;
    fieldConfig: FieldConfig;
    nestedFieldName?: string;
    groupName?: string;
    values?: Record<string, unknown>;
    dynamicFields?: FieldConfig[];
    setFieldValue?: (field: string, value: unknown) => void;
    handleInputChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>, setFieldValue: (field: string, value: unknown) => void, fieldName: string) => void;
    getMeasurementUnit: (unit: string[] | undefined) => string;
    t: (key: string, options?: Record<string, unknown>) => string;
    unitSystem?: string;
    renderErrorMessage?: (nestedFieldName: string) => JSX.Element;
    convertAndSetValues?: (newToggleValue: string, currentValues: Record<string, unknown>) => void;
}

/**
 * Helper component to render field labels with optional info box
 */
const FieldLabel: React.FC<{
    htmlFor: string;
    fieldConfig: FieldConfig;
    getMeasurementUnit: (unitArray: string[] | undefined) => string;
    t: (key: string, options?: Record<string, unknown>) => string; // i18n translation function
    showSymbol?: boolean;
}> = ({ htmlFor, fieldConfig, getMeasurementUnit, t, showSymbol }) => (
    <label htmlFor={htmlFor} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <span>
            {t(fieldConfig.label || '', {
                unit_measure: getMeasurementUnit(Array.isArray(fieldConfig.unit) ? fieldConfig.unit : undefined),
            })}
            {showSymbol && (
                <span className="input-symbol" style={{ marginLeft: '8px' }}>
                    <FormatSymbol symbol={fieldConfig.symbol!} />
                </span>
            )}
        </span>
        {fieldConfig.infoBoxImgSrc && (
            <InfoBoxButton
                imageSrc={fieldConfig.infoBoxImgSrc}
                tooltipText={t(`${fieldConfig.name}_info_tooltip`, {})}
                dialogTitle={t(`${fieldConfig.name}_info_title`, {}) || fieldConfig.label}
                imageAlt={t(`${fieldConfig.name}_info_alt`, {}) || `${fieldConfig.label} information`}
                size="small"
            />
        )}
        {fieldConfig.infoBoxTableData && (
            <InfoBoxButton
                tableData={fieldConfig.infoBoxTableData}
                tooltipText={t(`${fieldConfig.name}_info_tooltip`, {}) || 'View material properties data'}
                dialogTitle={t(`${fieldConfig.name}_info_title`, {}) || fieldConfig.label}
                size="small"
            />
        )}
    </label>
);

/**
 * Props interface for text field renderer
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface TextFieldRendererProps extends FieldRendererProps {
    handleInputChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>, setFieldValue: (field: string, value: unknown) => void, fieldName: string) => void;
    getMeasurementUnit: (unit: string[] | undefined) => string;
    renderErrorMessage?: (nestedFieldName: string) => JSX.Element;
}

/**
 * Helper function to get dynamic minimum value for a field
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getDynamicMinimum = (fieldConfig: FieldConfig, values: Record<string, unknown>, nestedFieldName: string): number => {
    if (!fieldConfig.dynamicMin) {
        return fieldConfig.min || 0;
    }

    const { sourceField, sourceGroup } = fieldConfig.dynamicMin;
    const sourceFieldPath = `${sourceGroup}.${sourceField}`;
    const sourceValue = getIn(values, sourceFieldPath);

    // Debug logging

    if (sourceValue !== undefined && sourceValue !== null) {
        const numericValue = Number(sourceValue);
        return numericValue;
    }

    return fieldConfig.min || 0;
};

/**
 * Renders toggle button group fields
 */
const ToggleFieldRenderer: React.FC<FieldRendererProps> = ({
    fieldConfig,
    nestedFieldName,
    groupName,
    values,
    dynamicFields,
    handleInputChange,
    getMeasurementUnit,
    t,
    unitSystem, // eslint-disable-line @typescript-eslint/no-unused-vars
    renderErrorMessage = defaultRenderErrorMessage,
}) => {
    const isDisabled = false;
    const finalFieldName = nestedFieldName || fieldConfig.name;

    // Generic toggle for all fields
    return (
        <div key={finalFieldName} className="field-container" data-testid={`field-${finalFieldName}`}>
            <FieldLabel htmlFor={finalFieldName} fieldConfig={fieldConfig} getMeasurementUnit={getMeasurementUnit} t={t} />
            <Box sx={{ mt: 1, mb: 1 }}>
                <Field name={finalFieldName} id={finalFieldName}>
                    {({ field, form }: { field: FieldInputProps<unknown>; form: FormikProps<unknown> }) => (
                        <Box>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                {fieldConfig.options?.map((option: { value: string | number; label: string }) => (
                                    <button
                                        key={option.value}
                                        type="button"
                                        className={`toggle-btn${field.value === option.value ? ' selected' : ''}`}
                                        onClick={() => {
                                            // Get current group values for mutually exclusive logic
                                            const currentGroupValues = groupName ? getIn(values, groupName) || {} : {};

                                            // Use the dynamicFields array directly (these are the fields for the current group)
                                            const groupFieldConfigs = dynamicFields || [];

                                            // Apply mutually exclusive logic
                                            const updatedGroupValues = ValidationService.handleMutuallyExclusiveFields(currentGroupValues, fieldConfig.name, option.value, groupFieldConfigs);

                                            // Update all changed fields in the group
                                            Object.keys(updatedGroupValues).forEach(fieldName => {
                                                const nestedPath = groupName ? `${groupName}.${fieldName}` : fieldName;
                                                form.setFieldValue(nestedPath, updatedGroupValues[fieldName]);
                                            });

                                            // NOTE: Cross-group mutual exclusion for Surface/Base/Subbase Design toggles
                                            // Required because these fields are in different form groups
                                            if (fieldConfig.name === 'enableSurfaceDesign' && option.value === 'yes') {
                                                form.setFieldValue('baseDesign.enableBaseDesign', 'no');
                                                form.setFieldValue('subbaseDesign.enableSubbaseDesign', 'no');
                                            } else if (fieldConfig.name === 'enableBaseDesign' && option.value === 'yes') {
                                                form.setFieldValue('surfaceDesign.enableSurfaceDesign', 'no');
                                                form.setFieldValue('subbaseDesign.enableSubbaseDesign', 'no');
                                            } else if (fieldConfig.name === 'enableSubbaseDesign' && option.value === 'yes') {
                                                form.setFieldValue('surfaceDesign.enableSurfaceDesign', 'no');
                                                form.setFieldValue('baseDesign.enableBaseDesign', 'no');
                                            }

                                            // Trigger custom change handler for special cases like ESAL modal
                                            if (handleInputChange) {
                                                const syntheticEvent = {
                                                    target: { value: option.value },
                                                } as React.ChangeEvent<HTMLInputElement>;
                                                handleInputChange(syntheticEvent, form.setFieldValue, fieldConfig.name);
                                            }
                                        }}
                                        style={{
                                            padding: '6px 16px',
                                            border: '1px solid #ccc',
                                            borderRadius: 4,
                                            background: field.value === option.value ? '#1976d2' : '#fff',
                                            color: field.value === option.value ? '#fff' : '#333',
                                            cursor: 'pointer',
                                        }}
                                        disabled={isDisabled}>
                                        {t(option.label)}
                                    </button>
                                ))}
                            </Box>
                        </Box>
                    )}
                </Field>
            </Box>
            <ErrorMessage name={finalFieldName} render={renderErrorMessage} />
        </div>
    );
};

/**
 * Renders select dropdown fields
 */
const SelectFieldRenderer: React.FC<FieldRendererProps> = ({
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    fieldName,
    fieldConfig,
    nestedFieldName,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setFieldValue,
    handleInputChange,
    getMeasurementUnit,
    t,
    unitSystem, // eslint-disable-line @typescript-eslint/no-unused-vars
    renderErrorMessage = defaultRenderErrorMessage,
}) => {
    const isMethodSelect = fieldName === 'calculationMethod';
    const isDisabled = false;
    const finalFieldName = nestedFieldName || fieldConfig.name;

    return (
        <div key={finalFieldName} className="field-container" data-testid={`field-${finalFieldName}`}>
            <FieldLabel htmlFor={finalFieldName} fieldConfig={fieldConfig} getMeasurementUnit={getMeasurementUnit} t={t} />
            <DynamicSelectField
                key={finalFieldName}
                nestedFieldName={finalFieldName}
                fieldConfig={fieldConfig}
                isMethodSelect={isMethodSelect}
                isDisabled={isDisabled}
                handleInputChange={handleInputChange}
                setFieldValue={setFieldValue || (() => {})} // eslint-disable-line @typescript-eslint/no-empty-function
                fieldName={fieldName || ''}
                getMeasurementUnit={getMeasurementUnit}
                t={t}
                renderErrorMessage={(msg: string) => renderErrorMessage(msg) as React.ReactElement}
            />
        </div>
    );
};

// 🔧 FIX: Separate component to handle slider logic with hooks outside Field render prop
interface SliderFieldLogicProps {
    fieldConfig: FieldConfig;
    dynamicMin: number;
    nestedFieldName: string;
    handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    getMeasurementUnit: (unitArray: string[] | undefined) => string;
}

const SliderFieldLogic: React.FC<SliderFieldLogicProps> = ({ fieldConfig, dynamicMin, nestedFieldName, handleInputChange, getMeasurementUnit }) => {
    return (
        <Field name={nestedFieldName}>
            {({ field, form }: { field: FieldInputProps<unknown>; form: FormikProps<unknown> }) => {
                // Text that is not a usable number stays in Formik so Yup blocks RUN and save:
                // the thumb shows the default but the sync below must never overwrite that text
                const holdsRawText = isPendingRawText(field.value, resolveDecimalFieldPolicy(fieldConfig, 'dynamic'));

                // Calculate current value ensuring it respects minimum constraint
                // Convert all values to numbers for slider component
                const fieldValue = holdsRawText ? 0 : typeof field.value === 'number' ? field.value : Number(field.value) || 0;
                const defaultValue = typeof fieldConfig.defaultValue === 'number' ? fieldConfig.defaultValue : Number(fieldConfig.defaultValue) || 0;
                const rawValue = fieldValue || defaultValue || dynamicMin;
                const currentValue = Math.max(rawValue, dynamicMin);

                // 🔧 FIX: Remove useEffect from render prop - handle value sync differently
                // Sync field value immediately if needed (without useEffect)
                if (!holdsRawText && currentValue !== field.value && currentValue > dynamicMin) {
                    // Use timeout to avoid direct state update during render
                    setTimeout(() => {
                        form.setFieldValue(nestedFieldName, currentValue);
                    }, 0);
                }

                return (
                    <SliderWithTextField
                        name={nestedFieldName}
                        value={currentValue}
                        min={dynamicMin}
                        max={fieldConfig.max || 100}
                        step={fieldConfig.step || 0.1}
                        unit={getMeasurementUnit(Array.isArray(fieldConfig.unit) ? fieldConfig.unit : undefined)}
                        onChange={(value: number) => {
                            form.setFieldValue(nestedFieldName, value);
                            // Also trigger handleInputChange for custom logic (e.g., ESAL calculations)
                            if (handleInputChange) {
                                const syntheticEvent = {
                                    target: { value: value.toString() },
                                } as React.ChangeEvent<HTMLInputElement>;
                                handleInputChange(syntheticEvent);
                            }
                        }}
                        onInvalidText={(raw: string) => form.setFieldValue(nestedFieldName, raw)}
                        resetKey={holdsRawText ? 'raw' : 'value'}
                        disabled={false}
                    />
                );
            }}
        </Field>
    );
};

/**
 * Renders slider fields with text input
 */
const SliderFieldRenderer: React.FC<FieldRendererProps> = ({
    fieldConfig,
    nestedFieldName,
    values,
    handleInputChange,
    getMeasurementUnit,
    t,
    unitSystem, // eslint-disable-line @typescript-eslint/no-unused-vars
    renderErrorMessage = defaultRenderErrorMessage,
}) => {
    const finalFieldName = nestedFieldName || fieldConfig.name;
    const dynamicMin = getDynamicMinimum(fieldConfig, values || {}, finalFieldName);

    return (
        <div key={finalFieldName} className="field-container" data-testid={`field-${finalFieldName}`}>
            <FieldLabel htmlFor={finalFieldName} fieldConfig={fieldConfig} getMeasurementUnit={getMeasurementUnit} t={t} showSymbol={false} />
            <Box sx={{ mt: 2, mb: 1 }}>
                <SliderFieldLogic
                    fieldConfig={fieldConfig}
                    dynamicMin={dynamicMin}
                    nestedFieldName={finalFieldName}
                    handleInputChange={handleInputChange ? (e: React.ChangeEvent<HTMLInputElement>) => handleInputChange(e, () => {}, '') : () => {}} // eslint-disable-line @typescript-eslint/no-empty-function
                    getMeasurementUnit={getMeasurementUnit}
                />
            </Box>
            <ErrorMessage name={finalFieldName} render={renderErrorMessage} />
        </div>
    );
};

/**
 * Renders text/number input fields
 */
const TextFieldRenderer: React.FC<FieldRendererProps> = ({
    fieldConfig,
    nestedFieldName,
    handleInputChange,
    getMeasurementUnit,
    t,
    unitSystem, // eslint-disable-line @typescript-eslint/no-unused-vars
    renderErrorMessage = defaultRenderErrorMessage,
}) => {
    const { values } = useFormikContext<DynamicFormData>();
    const isFieldReadOnly = (nestedFieldName || fieldConfig.name) === 'soilProperties.cbrOfBallast' ? getBallastCbr(values.materialTypes?.ballastType) !== undefined : fieldConfig.isReadOnly || false;
    const isDisabled = false;
    const showSymbol = fieldConfig.symbol && fieldConfig.renderAs !== 'select' && fieldConfig.renderAs !== 'toggleButtonGroup' && fieldConfig.renderAs !== 'slider';
    const finalFieldName = nestedFieldName || fieldConfig.name;

    // Get projectUnitSystem from Redux for unit display
    const projectUnitFromState = useAppSelector(state => state.Dynamic.project.data?.unit) as 'Metric' | 'Imperial' | undefined;

    return (
        <div key={finalFieldName} className="field-container" data-testid={`field-${finalFieldName}`}>
            <FieldLabel htmlFor={finalFieldName} fieldConfig={fieldConfig} getMeasurementUnit={getMeasurementUnit} t={t} showSymbol={false} />
            <div className={showSymbol ? 'input-container' : ''}>
                <Field
                    id={finalFieldName}
                    name={finalFieldName}
                    type={fieldConfig.type === 'string' && fieldConfig.renderAs !== 'textarea' ? 'text' : fieldConfig.type}
                    step={fieldConfig.step || (fieldConfig.type === 'number' ? '0.01' : undefined)}
                    className={showSymbol ? 'input-with-symbol' : ''}
                    disabled={isDisabled || isFieldReadOnly}
                    readOnly={isFieldReadOnly}
                    style={isFieldReadOnly ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : undefined}
                    as={fieldConfig.renderAs === 'textarea' ? 'textarea' : undefined}>
                    {({ field, form }: { field: FieldInputProps<unknown>; form: FormikProps<unknown> }) => (
                        <FormattedInput
                            field={field}
                            form={form}
                            fieldConfig={fieldConfig}
                            handleInputChange={handleInputChange}
                            nestedFieldName={finalFieldName}
                            showSymbol={!!showSymbol}
                            isDisabled={isDisabled}
                            isFieldReadOnly={isFieldReadOnly}
                            unitArray={fieldConfig.unit}
                            projectUnitSystem={projectUnitFromState || 'Metric'}
                        />
                    )}
                </Field>
                {showSymbol && <span className="input-symbol"><FormatSymbol symbol={fieldConfig.symbol!} /></span>}
            </div>
            <ErrorMessage name={finalFieldName} render={renderErrorMessage} />
        </div>
    );
};

/**
 * Renders custom fields based on field name patterns
 * These are VIRTUAL fields - they render visually but don't participate in form state
 */
const CustomFieldRenderer: React.FC<FieldRendererProps> = ({
    fieldConfig,
    t,
    unitSystem, // eslint-disable-line @typescript-eslint/no-unused-vars
}) => {
    // Handle railway train header fields - VIRTUAL RENDERING (no form state)
    if (fieldConfig.name.endsWith('Header')) {
        return (
            <div key={fieldConfig.name} className="field-container" style={{ margin: '24px 0 12px 0' }}>
                <div
                    style={{
                        padding: '8px 12px',
                        backgroundColor: '#f5f5f5',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontWeight: 'bold',
                        color: '#333',
                        fontSize: '14px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                    }}>
                    {t(fieldConfig.label || fieldConfig.name)}
                </div>
            </div>
        );
    }

    // Default custom rendering - simple text display
    return (
        <div key={fieldConfig.name} className="field-container">
            <div style={{ padding: '8px 0', fontStyle: 'italic', color: '#666' }}>{t(fieldConfig.label || fieldConfig.name)}</div>
        </div>
    );
};

/**
 * Main FieldRenderer component that delegates to specific renderers based on field type
 */
const FieldRenderer: React.FC<FieldRendererProps> = props => {
    const { fieldConfig } = props;
    const propsWithDefaults = {
        ...props,
        renderErrorMessage: props.renderErrorMessage || defaultRenderErrorMessage,
    };

    if (!fieldConfig) return null;

    // Factory pattern to render different field types
    const renderByType = () => {
        switch (fieldConfig.renderAs) {
            case 'toggleButtonGroup':
                if (fieldConfig.options) {
                    return <ToggleFieldRenderer {...propsWithDefaults} />;
                }
                break;
            case 'select':
                return <SelectFieldRenderer {...propsWithDefaults} />;
            case 'slider':
                return <SliderFieldRenderer {...propsWithDefaults} />;
            case 'custom':
                return <CustomFieldRenderer {...propsWithDefaults} />;
            default:
                return <TextFieldRenderer {...propsWithDefaults} />;
        }

        // Fallback to text field if no specific renderer matches
        return <TextFieldRenderer {...propsWithDefaults} />;
    };

    return renderByType();
};

export default React.memo(FieldRenderer);
